import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  CigaretteOff,
  ClipboardList,
  DollarSign,
  Flame,
  Trophy,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  fetchProgressDashboard,
  markTodaySmokeFree,
} from "../services/progressTrackingApi";
import { formatDisplayDate } from "../utils/dateFormat";
import "./ProgressTrackingPage.css";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function getActivityIcon(activityType = "") {
  if (activityType.startsWith("quit_plan")) return <ClipboardList />;
  if (activityType.startsWith("checkin")) return <CigaretteOff />;
  return <Check />;
}

function getActivityTitle(item) {
  return item.title || "Activity saved";
}

function getActivitySubtitle(item) {
  const dateLabel = formatDisplayDate(item.activity_at || item.progress_date);
  const subtitle =
    item.subtitle ||
    (item.status === "SMOKE_FREE" ? "Smoke-free day" : item.status || "");

  return [dateLabel, subtitle].filter(Boolean).join(" · ");
}

function formatDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getMonthLabel(date) {
  return date.toLocaleString("en-AU", {
    month: "long",
    year: "numeric",
  });
}

function buildCalendarCells(currentMonthDate) {
  const year = currentMonthDate.getFullYear();
  const month = currentMonthDate.getMonth();

  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);

  const startWeekday = firstDayOfMonth.getDay();
  const totalDays = lastDayOfMonth.getDate();

  const cells = [];

  for (let i = 0; i < startWeekday; i += 1) {
    cells.push(null);
  }

  for (let day = 1; day <= totalDays; day += 1) {
    cells.push(new Date(year, month, day));
  }

  return cells;
}

const ProgressTrackingPage = () => {
  const { loading: authLoading, isLoggedIn } = useAuth();

  const [currentMonthDate, setCurrentMonthDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const year = currentMonthDate.getFullYear();
  const month = currentMonthDate.getMonth() + 1;

  const loadDashboard = useCallback(async () => {
    if (!isLoggedIn) return;

    try {
      setError("");
      const data = await fetchProgressDashboard(year, month);
      setDashboard(data);
    } catch (err) {
      setError(err.message || "Failed to load progress tracking.");
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, year, month]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (authLoading) return;

      if (!isLoggedIn) {
        setDashboard(null);
        setLoading(false);
        setError("");
        return;
      }

      try {
        setLoading(true);
        setError("");
        const data = await fetchProgressDashboard(year, month);
        if (!cancelled) {
          setDashboard(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load progress tracking.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [authLoading, isLoggedIn, year, month]);

  const completedDateSet = useMemo(() => {
    return new Set(dashboard?.completed_dates || []);
  }, [dashboard]);

  const recentActivities = dashboard?.recent_activities || dashboard?.recent_history || [];

  const calendarCells = useMemo(() => {
    return buildCalendarCells(currentMonthDate);
  }, [currentMonthDate]);

  const todayKey = formatDateKey(new Date());

  const handleMarkToday = async () => {
    try {
      setMarking(true);
      setError("");
      setSuccessMessage("");

      await markTodaySmokeFree();

      setSuccessMessage("Today has been marked as smoke-free.");
      await loadDashboard();
    } catch (err) {
      setError(err.message || "Failed to save today’s progress.");
    } finally {
      setMarking(false);
    }
  };

  const goToPreviousMonth = () => {
    setCurrentMonthDate(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1),
    );
  };

  const goToNextMonth = () => {
    setCurrentMonthDate(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1),
    );
  };

  if (authLoading || loading) {
    return (
      <main className="progress-page">
        <div className="progress-page__container">
          <p className="progress-page__status">Loading progress tracking...</p>
        </div>
      </main>
    );
  }

  if (!isLoggedIn) {
    return (
      <main className="progress-page">
        <div className="progress-page__container">
          <section className="progress-page__auth-card" aria-label="Sign in required">
            <h1 className="progress-page__title">Progress Tracking</h1>
            <p className="progress-page__subtitle">
              Sign in to view your streaks, smoke-free days, saved money, and check-in history.
            </p>
            <Link
              to="/login"
              state={{ from: "/progress-tracking" }}
              className="progress-page__auth-button"
            >
              Sign in to view progress
            </Link>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main className="progress-page">
      <div className="progress-page__container">
        <Link to="/dashboard" className="progress-page__back-link">
          <ArrowLeft aria-hidden="true" />
          Back to Dashboard
        </Link>

        <header className="progress-page__header progress-page__header--with-action">
        <div>
          <h1 className="progress-page__title">Progress Tracking</h1>
          <p className="progress-page__subtitle">
            Track your journey with detailed insights and a calendar view.
          </p>
        </div>

        <Link to="/check-in-history" className="progress-page__history-link">
          View Check-In History
        </Link>
      </header>

        {error && (
          <p className="progress-page__error" role="alert">
            {error}
          </p>
        )}

        {successMessage && (
          <p className="progress-page__success" role="status">
            {successMessage}
          </p>
        )}

        <section className="progress-stats" aria-label="Progress summary">
          <article className="progress-stat-card">
            <div className="progress-stat-card__icon"><Flame /></div>
            <h2 className="progress-stat-card__value">
              {dashboard?.current_streak ?? 0}
            </h2>
            <p className="progress-stat-card__label">Current Streak</p>
          </article>

          <article className="progress-stat-card">
            <div className="progress-stat-card__icon"><Trophy /></div>
            <h2 className="progress-stat-card__value">
              {dashboard?.best_streak ?? 0}
            </h2>
            <p className="progress-stat-card__label">Best Streak</p>
          </article>

          <article className="progress-stat-card">
            <div className="progress-stat-card__icon"><DollarSign /></div>
            <h2 className="progress-stat-card__value">
              ${dashboard?.money_saved ?? 0}
            </h2>
            <p className="progress-stat-card__label">Money Saved</p>
          </article>

          <article className="progress-stat-card">
            <div className="progress-stat-card__icon"><CigaretteOff /></div>
            <h2 className="progress-stat-card__value">
              {dashboard?.smoke_free_days ?? 0}
            </h2>
            <p className="progress-stat-card__label">Smoke-Free Days</p>
          </article>
        </section>

        <section className="progress-calendar-card">
          <div className="progress-calendar-card__top">
            <div>
              <h2 className="progress-calendar-card__title">
                Monthly Progress Calendar
              </h2>
              <p className="progress-calendar-card__subtitle">
                {getMonthLabel(currentMonthDate)}
              </p>
            </div>

            <div className="progress-calendar-card__nav">
              <button
                type="button"
                className="calendar-nav-btn"
                onClick={goToPreviousMonth}
                aria-label="Previous month"
              >
                <ChevronLeft aria-hidden="true" />
              </button>
              <button
                type="button"
                className="calendar-nav-btn"
                onClick={goToNextMonth}
                aria-label="Next month"
              >
                <ChevronRight aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="calendar-weekdays">
            {DAYS.map((day) => (
              <div key={day} className="calendar-weekdays__item">
                {day}
              </div>
            ))}
          </div>

          <div className="calendar-grid">
            {calendarCells.map((date, index) => {
              if (!date) {
                return (
                  <div
                    key={`empty-${index}`}
                    className="calendar-cell calendar-cell--empty"
                  />
                );
              }

              const dateKey = formatDateKey(date);
              const isCompleted = completedDateSet.has(dateKey);
              const isToday = dateKey === todayKey;

              return (
                <div
                  key={dateKey}
                  className={[
                    "calendar-cell",
                    isCompleted ? "calendar-cell--completed" : "",
                    isToday ? "calendar-cell--today" : "",
                  ].join(" ")}
                >
                  <span className="calendar-cell__date">{date.getDate()}</span>
                  {isCompleted && (
                    <span className="calendar-cell__check"><Check /></span>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <section className="progress-history-card">
          <h2 className="progress-history-card__title">Recent Activities</h2>

          {recentActivities.length ? (
            <ul className="progress-history-list">
              {recentActivities.map((item) => (
                <li
                  key={item.activity_id || item.progress_id}
                  className="progress-history-item"
                >
                  <div className="progress-history-item__left">
                    <div className="progress-history-item__tick">
                      {getActivityIcon(item.activity_type)}
                    </div>
                    <div>
                      <p className="progress-history-item__date">
                        {getActivityTitle(item)}
                      </p>
                      <p className="progress-history-item__meta">
                        {getActivitySubtitle(item)}
                      </p>
                    </div>
                  </div>

                  <span className="progress-history-item__badge">
                    {item.badge || "SAVED"}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="progress-history-card__empty">
              No recent activity yet. Mark progress, save a check-in, or create a quit plan to begin tracking.
            </p>
          )}
        </section>

        <div className="progress-page__cta">
          <button
            type="button"
            className="progress-page__button"
            onClick={handleMarkToday}
            disabled={marking || dashboard?.completed_today}
          >
            {dashboard?.completed_today
              ? "Today Already Marked"
              : marking
                ? "Saving..."
                : "I Didn’t Smoke Today"}
          </button>
        </div>
      </div>
    </main>
  );
};

export default ProgressTrackingPage;
